import { useSearchParams } from 'react-router-dom';

import LoadingState from 'components/LoadingState';
import StackedLayout from 'layouts/StackedLayout';
import TabGroup from 'lib/components/Tabs';
import { EOrganization, ESnapshot, ESnapshotExists, EUser } from 'lib/types';
import InvoiceTable from './InvoicesTable';
import PublisherInvoicesTable from './PublisherInvoicesTable';
import usePublisherInvoicesTabEnabled from './hooks/usePublisherInvoicesTabEnabled';
import {
  CUSTOMER_INVOICES_TAB,
  getInvoicesTabById,
  INVOICES_TAB_QUERY_PARAM,
  INVOICES_TABS,
  PUBLISHER_INVOICES_TAB
} from './InvoiceTableTabs';

type InvoicesTabbedViewProps = {
  user: ESnapshotExists<EUser>;
  organization: ESnapshot<EOrganization> | null;
  usesBulkInvoiceV2: boolean;
};

/**
 * Renders the invoices page. When the publisher invoice table flag is on and
 * the active organization is (or inherits from) a publisher configured for
 * Column invoicing, the page shows Customer Invoices and Publisher Invoices
 * tabs; otherwise it renders the customer invoices table without tabs. The
 * active tab is driven by the `tab` query param so it can be deep-linked.
 */
export default function InvoicesTabbedView({
  user,
  organization,
  usesBulkInvoiceV2
}: InvoicesTabbedViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { enabled: showPublisherTab, isLoading } =
    usePublisherInvoicesTabEnabled(organization);

  // Avoid a tab-bar flash while the inherited configuration resolves
  if (isLoading) {
    return <LoadingState />;
  }

  const requestedTab = getInvoicesTabById(
    searchParams.get(INVOICES_TAB_QUERY_PARAM)
  );
  const activeTab =
    requestedTab &&
    (requestedTab.id !== PUBLISHER_INVOICES_TAB.id || showPublisherTab)
      ? requestedTab
      : CUSTOMER_INVOICES_TAB;

  return (
    <StackedLayout>
      <header className="flex justify-between pb-6 items-center">
        <div>
          <h1 className="text-2xl font-medium leading-tight text-column-gray-900 mb-1">
            Invoices
          </h1>
          <h2 className="text-column-gray-400 leading-6">
            Track and manage invoices associated with your account.
          </h2>
        </div>
      </header>
      <main className="bg-white sm:rounded-lg border border-column-gray-100 shadow-column-2">
        {showPublisherTab && (
          <TabGroup
            onClickTab={tab =>
              setSearchParams(prev => {
                prev.set(INVOICES_TAB_QUERY_PARAM, tab.id);
                return prev;
              })
            }
            activeTab={activeTab}
            tabs={INVOICES_TABS}
            id="invoices-tabs"
          />
        )}
        {activeTab.id === PUBLISHER_INVOICES_TAB.id ? (
          <PublisherInvoicesTable organization={organization} />
        ) : (
          <InvoiceTable {...{ user, organization, usesBulkInvoiceV2 }} />
        )}
      </main>
    </StackedLayout>
  );
}
